import { apiFetch } from './ApiService.js';
import type { IToastService } from './ToastService.js';
import type { IModalService } from './ModalService.js';

/// State + drift for `_DeleteConfirm.cshtml`-modalen.
///
/// Brugte mønster: caller invokerer `open({...})` med expected-navn + action-URL.
/// Modalen renderes med data-bound felter; primær-knap forbliver disabled
/// indtil input matcher expected EXACT (case-sensitiv, samme som GitHub).
/// Klik på "Slet endeligt" → POST → toast → navigér eller reload.
///
/// `MODAL_ID` er hardkodet — vi har max én delete-flow aktiv ad gangen.

export interface DeleteConfirmOptions {
  title: string;
  message: string;
  expectedName: string;
  actionUrl: string;
  successMsg: string;
  /// Hvor klienten skal hen efter success. Default: window.location.reload().
  /// Pas en URL for at navigere (typisk efter slet-bruger eller slet-domæne,
  /// hvor detail-siden ikke længere eksisterer).
  redirectTo?: string;
}

interface BrokerResponse { ok: boolean; detail?: string | null; }

export interface IModalConfirmHelper {
  open(opts: DeleteConfirmOptions): void;
  confirm(): Promise<void>;
}

export class ModalConfirmHelper implements IModalConfirmHelper {
  private static readonly MODAL_ID = 'modal-delete-confirm';
  private pending: DeleteConfirmOptions | null = null;

  constructor(
    private readonly toast: IToastService,
    private readonly modal: IModalService,
    private readonly doc: Document = document,
  ) {
    this.attachInputListener();
  }

  open(opts: DeleteConfirmOptions): void {
    const modal = this.doc.getElementById(ModalConfirmHelper.MODAL_ID);
    if (!modal) {
      console.warn(`ModalConfirmHelper: '${ModalConfirmHelper.MODAL_ID}' findes ikke på siden`);
      return;
    }
    this.pending = opts;

    const titleEl = modal.querySelector<HTMLElement>('[data-delete-title]');
    const msgEl = modal.querySelector<HTMLElement>('[data-delete-message]');
    const expectedEl = modal.querySelector<HTMLElement>('[data-delete-expected]');
    const inputEl = modal.querySelector<HTMLInputElement>('[data-field="confirm-name"]');
    const btnEl = modal.querySelector<HTMLButtonElement>('[data-action="confirm-delete"]');

    if (titleEl) titleEl.textContent = opts.title;
    if (msgEl) msgEl.textContent = opts.message;
    if (expectedEl) expectedEl.textContent = opts.expectedName;
    if (inputEl) inputEl.value = '';
    if (btnEl) btnEl.disabled = true;

    this.modal.open(ModalConfirmHelper.MODAL_ID);
    // Fokus så brugeren kan skrive med det samme.
    setTimeout(() => inputEl?.focus(), 50);
  }

  async confirm(): Promise<void> {
    if (this.pending === null) return;
    const opts = this.pending;
    try {
      const resp = await apiFetch(opts.actionUrl, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Accept': 'application/json' },
      });
      const body = await this.readBody(resp);
      if (resp.ok && body.ok) {
        this.toast.show(opts.successMsg, 'success');
        this.modal.close(ModalConfirmHelper.MODAL_ID);
        if (opts.redirectTo) {
          window.location.href = opts.redirectTo;
        } else {
          window.location.reload();
        }
        return;
      }
      const detail = body.detail ?? `HTTP ${resp.status}`;
      this.toast.show(`Sletning fejlede: ${detail}`, 'error');
    } catch (e) {
      console.error(e);
      this.toast.show('Netværksfejl — kunne ikke nå Admin-server', 'error');
    } finally {
      this.pending = null;
    }
  }

  /// Lyt på input-ændringer i modalen — toggle primær-knap-disabled efter
  /// match. Vi tilknytter listener én gang ved construct (delegering via
  /// document.input-event er enklere end at re-attache ved hver open()).
  private attachInputListener(): void {
    this.doc.addEventListener('input', (e) => {
      const target = e.target as HTMLInputElement | null;
      if (!target?.matches('[data-field="confirm-name"]')) return;
      const modal = this.doc.getElementById(ModalConfirmHelper.MODAL_ID);
      if (!modal || !modal.classList.contains('active')) return;
      const expected = modal.querySelector<HTMLElement>('[data-delete-expected]')?.textContent ?? '';
      const btn = modal.querySelector<HTMLButtonElement>('[data-action="confirm-delete"]');
      if (btn) btn.disabled = target.value !== expected;
    });
  }

  private async readBody(resp: Response): Promise<BrokerResponse> {
    try { return await resp.json() as BrokerResponse; }
    catch { return { ok: false, detail: `HTTP ${resp.status}` }; }
  }
}
